import React, { lazy, memo, useEffect, useMemo } from 'react'
import Condition from 'components/Condition'
import ConditionSwitch from 'components/ConditionSwitch'
import PagePlaceholder from 'components/PagePlaceholder'
import Suspense from 'components/Suspense'
import FreelancerProfile from 'scenes/FreelancerProfile'
import JobsListing from 'scenes/JobsListing'
import Job from 'scenes/Job'
import { graphql, PreloadedQuery, usePreloadedQuery } from 'react-relay'
import { SlugViewer_Query } from '__generated__/SlugViewer_Query.graphql'
import { useDispatch } from 'hooks/redux'
import { isPrerendering } from 'services/prerender'
import { setHeaderVariant } from 'components/Layout/LayoutDucks'

const PlatformAccessLimiter = lazy(() => import(/* webpackChunkName: "PlatformAccessLimiter" */ 'components/PlatformAccessLimiter'))

export const SlugViewerQuery = graphql`
  query SlugViewer_Query(
    $slug: String!,
    $subslug: String,
    $token: String,
    $ref: String,
    $referrer: String,
    $requestValid: Boolean!
  ) {
    resource(slug: $slug, subslug: $subslug, token: $token, ref: $ref, referrer: $referrer) @include(if: $requestValid) {
      __typename

      ... on Job {
        ...Job_Job
        
        firm {
          backgroundTheme
        }
      }

      ... on Firm {
        ...JobsListing_Firm
      }

      ... on User {
        ...FreelancerProfile_UserScene
      }
    }
    
    currentUser {
      ...Job_User
      ...JobsListing_User
    }
  }
`

function SlugViewer({ preloadedQuery }: { preloadedQuery: PreloadedQuery<SlugViewer_Query>}) {
  const data = usePreloadedQuery(SlugViewerQuery, preloadedQuery)
  const dispatch = useDispatch()

  const resource = data?.resource

  useEffect(() => {
    if (resource?.__typename === 'Job') {
      if (resource.firm?.backgroundTheme === 'light' && isPrerendering()) dispatch(setHeaderVariant({ variant: 'default' }))
    }
  }, [resource])

  const content = useMemo(() => {
    if (resource?.__typename === 'Job') {
      return <Job jobFragmentRef={resource} userFragmentRef={data?.currentUser || null} />
    }

    if (resource?.__typename === 'User') {
      return <FreelancerProfile user={resource} />
    }

    if (resource?.__typename === 'Firm') {
      return <JobsListing firmFragmentRef={resource} userFragmentRef={data?.currentUser || null} />
    }

    return null
  }, [resource, data?.currentUser])

  return (
    <React.Fragment>
      <Suspense noSsr>
        <PlatformAccessLimiter />
      </Suspense>

      <ConditionSwitch>
        <Condition condition={content !== null}>
          {content}
        </Condition>

        <Condition condition>
          <PagePlaceholder
            flat
            title="Not found"
          />
        </Condition>
      </ConditionSwitch>

    </React.Fragment>
  )
}

export default memo(SlugViewer)
